import axios from "axios";
import React, { createContext, useState, useMemo } from "react";
import { ADMIN, API_ENDPOINT } from "../utils/constants";
import { extractDate, extractTime, formatVisitorForExcel, toastError } from "../utils/util";
import * as XLSX from "xlsx";
import { getHeader } from "../utils/utils2";

export const ExportContext = createContext();

const MAX_CELL_LENGTH = 32767;

export const ExportProvider = ({ children }) => {
  const [visitorExporting, setVisitorExporting] = useState(false);
  const [exhibitorExporting, setExhibitorExporting] = useState(false);
  const [tmExporting, setTmExporting] = useState(false);
  const [oesExporting, setOesExporting] = useState(false);
  const [productExporting, setProductExporting] = useState(false);

  const exportVisitors = async (type = "visitor", sourceFilter = "", sq = "", customData = null) => {
    try {
      setVisitorExporting(true);

      const targetType = type || "visitor";

      if (Array.isArray(customData) && customData.length > 0) {
        const formattedData = customData.map((item) =>
          formatVisitorForExcel(item)
        );
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Visitors");
        XLSX.writeFile(wb, `${targetType}_export.xlsx`);
        return;
      }

      let allRecords = [];

      try {
        let page = 1;
        let hasMore = true;
        const pageSize = 1000;
        const seenIds = new Set();
        let expectedTotal = null;

        while (hasMore && page <= 500) {
          let url = `${API_ENDPOINT}/admin/get-visitors?type=${targetType}&page=${page}&limit=${pageSize}`;
          if (sourceFilter && sourceFilter !== "ALL") {
            url += `&sourceType=${encodeURIComponent(sourceFilter)}`;
          }
          if (sq) {
            url += `&sq=${encodeURIComponent(sq)}`;
          }

          const jsonRes = await axios.get(url, getHeader(ADMIN));
          const resData = jsonRes?.data || {};

          let batch = [];
          if (Array.isArray(resData)) {
            batch = resData;
          } else if (Array.isArray(resData.data)) {
            batch = resData.data;
          } else if (Array.isArray(resData.visitors)) {
            batch = resData.visitors;
          } else if (Array.isArray(resData.result)) {
            batch = resData.result;
          }

          const totalFromRes =
            resData.totalItems ??
            resData.total ??
            resData.totalCount;
          if (totalFromRes !== undefined && totalFromRes !== null) {
            expectedTotal = Number(totalFromRes);
          }

          if (!batch || batch.length === 0) {
            hasMore = false;
          } else {
            let newItemsAdded = 0;
            for (const item of batch) {
              const rawId = item._id || item.user_id || item.barcode_id || item.id;
              const idKey = rawId
                ? (typeof rawId === "object" ? JSON.stringify(rawId) : String(rawId))
                : JSON.stringify(item);
              if (!seenIds.has(idKey)) {
                seenIds.add(idKey);
                allRecords.push(item);
                newItemsAdded++;
              }
            }

            if (expectedTotal !== null && allRecords.length >= expectedTotal) {
              hasMore = false;
            } else if (batch.length < pageSize && newItemsAdded > 0) {
              if (expectedTotal === null) {
                hasMore = false;
              } else {
                page++;
              }
            } else if (newItemsAdded === 0) {
              hasMore = false;
            } else {
              page++;
            }
          }
        }
      } catch (e) {
        console.warn("JSON fetch fallback to blob endpoint", e);
      }

      if (Array.isArray(allRecords) && allRecords.length > 0) {
        const formattedData = allRecords.map((item) =>
          formatVisitorForExcel(item)
        );
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Visitors");
        XLSX.writeFile(wb, `${targetType}_export.xlsx`);
        return;
      }

      // 1. Fetch the binary Excel file from the backend
      const config = {
        ...getHeader(),
        responseType: "blob",
      };

      const response = await axios.get(
        `${API_ENDPOINT}/admin/get-export-data/${type}`,
        config
      );

      const contentDisposition = response.headers["content-disposition"];
      let filename = `${type}_export.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);

      if (
        error.response &&
        error.response.data &&
        error.response.data instanceof Blob &&
        error.response.data.type === "application/json"
      ) {
        const reader = new FileReader();
        reader.onload = () => {
          const errorData = JSON.parse(reader.result);
          toastError({ response: { data: errorData } });
        };
        reader.readAsText(error.response.data);
      } else {
        toastError(error);
      }
    } finally {
      setVisitorExporting(false);
    }
  };

  const exportExhibitors = async (customData = null) => {
    try {
      setExhibitorExporting(true);
      let data = customData;
      if (!Array.isArray(data)) {
        const res = await axios.get(
          `${API_ENDPOINT}/user/a/get-all-exhibitors`,
          getHeader()
        );
        data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
      }

      const filteredData = data.map((item) => {
        const catList = item.productCategories?.value || item.productCategories || [];
        const formattedString = (Array.isArray(catList) ? catList : [])
          .map((cat) => (typeof cat === "string" ? cat : cat.subSubCat || cat.mainCat || ""))
          .filter(Boolean)
          .join(", ");

        const aboutString = (item.about?.value || item.about || "").replace(
          /<\/?[^>]+(>|$)/g,
          ""
        );

        return {
          "Exhibitor ID": item.user_id || item._id,
          profile_image: item.profile_image || "",
          Username: item.username || "",
          Password: item.password || "",
          [item.companyName?.label || "Company Name"]: item.companyName?.value || item.companyName || "",
          [item.about?.label || "About"]: aboutString,
          [item.contactSalutation?.label || "Contact Salutation"]:
            item.contactSalutation?.value || item.contactSalutation || "",
          [item.contactFirstName?.label || "First Name"]:
            item.contactFirstName?.value || item.contactFirstName || "",
          [item.contactLastName?.label || "Last Name"]:
            item.contactLastName?.value || item.contactLastName || "",
          [item.contactDesignation?.label || "Designation"]:
            item.contactDesignation?.value || item.contactDesignation || "",
          [item.mobile?.label || "Mobile"]: item.mobile?.value || item.mobile || "",
          [item.companyEmail?.label || "Company Email"]:
            item.companyEmail?.value || item.companyEmail || "",
          [item.contactEmail?.label || "Contact Email"]:
            item.contactEmail?.value || item.contactEmail || "",
          [item.address1?.label || "Address 1"]: item.address1?.value || item.address1 || "",
          [item.address2?.label || "Address 2"]: item.address2?.value || item.address2 || "",
          [item.address3?.label || "Address 3"]: item.address3?.value || item.address3 || "",
          [item.country?.label || "Country"]: (typeof item.country?.value === "object" ? item.country?.value?.name : item.country?.value || item.country || ""),
          [item.state?.label || "State"]: (typeof item.state?.value === "object" ? item.state?.value?.name : item.state?.value || item.state || ""),
          [item.city?.label || "City"]: (typeof item.city?.value === "object" ? item.city?.value?.name : item.city?.value || item.city || ""),
          [item.pin?.label || "Pin"]: item.pin?.value || item.pin || "",
          [item.phone?.label || "Phone"]: item.phone?.value || item.phone || "",
          [item.fax?.label || "Fax"]: item.fax?.value || item.fax || "",
          [item.website?.label || "Website"]: item.website?.value || item.website || "",
          [item.companyShortName?.label || "Short Name"]:
            item.companyShortName?.value || item.companyShortName || "",
          [item.fascia?.label || "Fascia"]: item.fascia?.value || item.fascia || "",
          [item.weAre?.label || "We Are"]: item.weAre?.value || item.weAre || "",
          [item.industrySector?.label || "Industry Sector"]: Array.isArray(item.industrySector?.value || item.industrySector)
            ? (item.industrySector?.value || item.industrySector).join(", ")
            : (item.industrySector?.value || item.industrySector || ""),
          [item.exhibitorCategory?.label || "Exhibitor Category"]:
            item.exhibitorCategory?.value || item.exhibitorCategory || "",
          [item.productCategories?.label || "Product Categories"]:
            formattedString,
          [item.hallNumber?.label || "Hall Number"]: item.hallNumber?.value || item.hallNumber || "",
          [item.standNumber?.label || "Stand Number"]: item.standNumber?.value || item.standNumber || "",
          [item.spaceType?.label || "Space Type"]: item.spaceType?.value || item.spaceType || "",
          [item.exhibitArrival?.label || "Exhibit Arrival"]:
            item.exhibitArrival?.value || item.exhibitArrival || "",
          [item.exhibitArrivalTimeFrom?.label || "Exhibit Arrival Time From"]:
            item.exhibitArrivalTimeFrom?.value || item.exhibitArrivalTimeFrom || "",
          [item.exhibitArrivalTimeTo?.label || "Exhibit Arrival Time To"]:
            item.exhibitArrivalTimeTo?.value || item.exhibitArrivalTimeTo || "",
          [item.materialArrival?.label || "Material Arrival"]:
            item.materialArrival?.value || item.materialArrival || "",
          [item.materialArrivalTimeFrom?.label || "Material Arrival Time From"]:
            item.materialArrivalTimeFrom?.value || item.materialArrivalTimeFrom || "",
          [item.materialArrivalTimeTo?.label || "Material Arrival Time To"]:
            item.materialArrivalTimeTo?.value || item.materialArrivalTimeTo || "",
          [item.dismantling?.label || "Dismantling"]: item.dismantling?.value || item.dismantling || "",
          [item.dismantlingTimeFrom?.label || "Dismantling Time From"]:
            item.dismantlingTimeFrom?.value || item.dismantlingTimeFrom || "",
          [item.dismantlingTimeTo?.label || "Dismantling Time To"]:
            item.dismantlingTimeTo?.value || item.dismantlingTimeTo || "",
          [item.standSize?.label || "Stand Size"]: item.standSize?.value || item.standSize || "",
        };
      });

      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Exhibitors");
      XLSX.writeFile(wb, "exhibitors.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setExhibitorExporting(false);
    }
  };

  const exportProducts = async (customData = null) => {
    try {
      setProductExporting(true);
      let data = customData;
      if (!Array.isArray(data)) {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-products`,
          getHeader(ADMIN)
        );
        data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
      }
      const filteredData = data?.map((item) => {
        const catList = item.selectedCategories?.value || item.selectedCategories || [];
        const myCats = (Array.isArray(catList) ? catList : []).reduce(
          (acc, cat) => {
            if (typeof cat === "string") {
              if (!acc["Category"]) acc["Category"] = [];
              acc["Category"].push(cat);
            } else if (cat && typeof cat === "object") {
              const mainCat = cat.mainCat || "Category";
              const subSubCat = cat.subSubCat || cat.subCat || "";
              if (!acc[mainCat]) {
                acc[mainCat] = [];
              }
              if (subSubCat) acc[mainCat].push(subSubCat);
            }
            return acc;
          },
          {}
        );
        const formattedString = Object.entries(myCats)
          .map(
            ([mainCat, subSubCats]) => `${mainCat}: ${subSubCats.join(", ")}`
          )
          .join(", ");

        return {
          "Object ID": item._id,
          Owner: item.owner || item.assignTo || "",
          image: (item.images || []).join(","),
          name: item.name || "",
          categories: formattedString,
          description: (item.description || "").slice(0, 200),
          brochure: item.brochure || "",
          assignTo: item.assignTo || "",
          productVideo: item.productVideo?.content || "",
          Date: extractDate(item.createdOn || item.createdAt),
          Time: extractTime(item.createdOn || item.createdAt),
        };
      });
      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");
      XLSX.writeFile(wb, "products.xlsx");
    } catch (error) {
      console.log(error);
      toastError(error);
    } finally {
      setProductExporting(false);
    }
  };

  const exportOes = async (customData = null) => {
    try {
      setOesExporting(true);
      let data = customData;
      if (!Array.isArray(data)) {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-oes-submit`,
          getHeader(ADMIN)
        );
        data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
      }

      const filteredData = data?.map((item) => {
        const rawFormData = item.form
          ? JSON.stringify(item.form)
          : item.user
          ? JSON.stringify(item.user)
          : item.formData
          ? JSON.stringify(item.formData)
          : "";

        const safeFormData =
          rawFormData.length > MAX_CELL_LENGTH
            ? rawFormData.substring(0, MAX_CELL_LENGTH - 3) + "..."
            : rawFormData;

        return {
          "Object ID": item._id,
          "Form Name": item.name || item.formName || "",
          "Submitted By": item.submittedBy || item.user?.username || item.companyName || "",
          "Submitted On": extractDate(item.createdOn || item.createdAt),
          Supplier: (item.selectedSupplier || item.supplier)?.supplierName || item.supplierName || "",
          "Form Data": safeFormData,
        };
      });
      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "OES");
      XLSX.writeFile(wb, "oes.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setOesExporting(false);
    }
  };

  const exportTm = async (customData = null) => {
    try {
      setTmExporting(true);
      let data = customData;
      if (!Array.isArray(data)) {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-teammembers`,
          getHeader(ADMIN)
        );
        data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
      }
      const filteredData = data?.map((item) => {
        return {
          "Object ID": item._id,
          Owner: item.owner || "",
          profile_image: item.profile_image || "",
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          phone: item.phone || "",
          email: item.email || "",
          role: item.role || "",
          designation: item.designation || "",
          Date: extractDate(item.createdOn || item.createdAt),
          Time: extractTime(item.createdOn || item.createdAt),
        };
      });
      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Team Members");
      XLSX.writeFile(wb, "team_members.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setTmExporting(false);
    }
  };

  // useMemo ensures the context object isn't recreated on every re-render
  // unless an actual exporting state changes.
  const contextValue = useMemo(
    () => ({
      exportVisitors,
      exportExhibitors,
      exhibitorExporting,
      visitorExporting,
      exportProducts,
      productExporting,
      exportOes,
      oesExporting,
      exportTm,
      tmExporting,
    }),
    [
      exhibitorExporting,
      visitorExporting,
      productExporting,
      oesExporting,
      tmExporting,
    ]
  );

  return (
    <ExportContext.Provider value={contextValue}>
      {children}
    </ExportContext.Provider>
  );
};
